import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import BasicAdvancedWrapper, {
  BasicAdvancedWrapperProps,
} from "../components/BasicAdvancedWrapper";
import { CreateModalProps } from "../index";
import {
  RubricGeneratorCreateRequestDto,
  RubricGeneratorCreateRequestDtoRubricType,
  RubricGeneratorCreateRequestDtoCefrLevel,
} from "@packages/api/madlenAPI.schemas";
import RubricSpecificFields from "./RubricSpecificFields";

interface RubricCreateModalMultiStepProps
  extends Omit<CreateModalProps, "onSubmit"> {
  onSubmit: (input: RubricGeneratorCreateRequestDto) => Promise<void>;
}

export const RubricCreateModal: React.FC<RubricCreateModalMultiStepProps> = ({
  onSubmit,
  initialInput,
  ...props
}) => {
  const { t } = useTranslation();

  // State for basic rubric fields
  const [basicRubricData, setBasicRubricData] = useState<{
    rubricType: RubricGeneratorCreateRequestDtoRubricType;
    totalScore: number;
    assignmentInstructions: string;
    cefrLevel?: RubricGeneratorCreateRequestDtoCefrLevel;
  }>({
    rubricType: RubricGeneratorCreateRequestDtoRubricType.ANALYTIC,
    totalScore: 70,
    assignmentInstructions: "",
    cefrLevel: RubricGeneratorCreateRequestDtoCefrLevel.A1,
  });

  // Handle basic mode submission (original behavior)
  const handleBasicSubmit = async (data: any) => {
    // Add basic rubric-specific fields from component state
    const submissionData = {
      ...data,
      rubricType: basicRubricData.rubricType,
      totalScore: basicRubricData.totalScore,
      assignmentInstructions:
        basicRubricData.assignmentInstructions || data.prompt, // Use assignment instructions if provided, otherwise use prompt
      cefrLevel: basicRubricData.cefrLevel,
    };

    await onSubmit(submissionData);
  };

  // Handle rubric-specific fields changes
  const handleRubricFieldsChange = useCallback(
    (rubricData: {
      rubricType: RubricGeneratorCreateRequestDtoRubricType;
      totalScore: number;
      assignmentInstructions: string;
      cefrLevel?: RubricGeneratorCreateRequestDtoCefrLevel;
    }) => {
      setBasicRubricData(rubricData);
    },
    [],
  );

  // Prepare the wrapper props
  const wrapperProps: BasicAdvancedWrapperProps<RubricGeneratorCreateRequestDto> =
    {
      createModalProps: {
        ...props,
        initialInput,
        themeColor: "green",
        onSubmit: handleBasicSubmit, // This will be set by the wrapper
        isRubricGenerator: true,
        after: (
          <RubricSpecificFields
            initialData={basicRubricData}
            onChange={handleRubricFieldsChange}
            themeColor="green"
          />
        ),
      },
      multiStepProps: {
        steps: [],
        initialData: {} as RubricGeneratorCreateRequestDto,
        title: t("quickActions.rubricCreate", "Create Rubric"),
        themeColor: "green",
        backPath: "/content/rubric",
        isLoading: props.isLoading,
        error: props.error,
        loadingTitle: props.loadingTitle,
        errorTitle: props.errorTitle,
        errorDescription: props.errorDescription,
        onFinalSubmit: () => Promise.resolve(),
        basicModeSubmitText: t("common.generate", "Generate"),
        advancedModeSubmitText: t(
          "rubric.createAdvanced",
          "Create Advanced Rubric",
        ),
      },
      themeColor: "green",
      title: t("quickActions.rubricCreate", "Create Rubric"),
      backPath: "/content/rubric",
      enableAdvancedMode: false,
      defaultMode: "basic",
      showModeToggle: false,
      onBasicSubmit: handleBasicSubmit,
      onAdvancedSubmit: () => Promise.resolve(),
    };

  return <BasicAdvancedWrapper {...wrapperProps} />;
};

export default RubricCreateModal;
